import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Paper,
  Autocomplete,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../redux/Slice/loaderSlice";
import { fetchRoles } from "../../redux/Slice/roleSlice";
import CloudUpload from "../../components/upload/CloudUpload";
import {
  createEmployee,
  fetchEmployeeByid,
  updateEmployee,
} from "../../redux/Slice/employeeSlice";
import { toast } from "sonner";
import { clearMessage } from "../../redux/Slice/message";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Country } from "country-state-city";
import { getCountryCallingCode } from "libphonenumber-js";

const NewEmployee = () => {
  const params = useParams();
  const navigate = useNavigate();
  const e_id = params?.id;
  const location = useLocation();
  const routeData = location.state;
  
  const [file, setFile] = useState(null);

  const dispatch = useDispatch();

  const { roles } = useSelector((state) => state.roles);
  const { employeeDetail } = useSelector((state) => state.employee);
  
  const message = useSelector((state) => state.message);
  const [selectedCountry, setSelectedCountry] = useState("");
  
  const countries = Country.getAllCountries().filter(country => {
    try {
      getCountryCallingCode(country.isoCode);
      return true;
    } catch (error) {
      return false;
    }
  });

  const roleOptions =
    roles &&
    roles
      .filter((role) => role?.status !== false)
      .map((role) => ({ name: role.name, id: role._id }));

  useEffect(() => {
    if (message?.message) {
      toast(message.message);
      dispatch(clearMessage());
    }
  }, [message, dispatch]);

  useEffect(() => {
    const params = { limit: 100 };
    dispatch(fetchRoles(params));

    if (e_id) {
      dispatch(fetchEmployeeByid({ e_id }));
    }
  }, [dispatch, e_id]);

  useEffect(() => {
    if (selectedCountry) {
      const country = countries.find((c) => c.name === selectedCountry);
      if (country) {
        try {
          const newCountryCode = `+${getCountryCallingCode(country.isoCode)}`;
          formik.setFieldValue("country_code", newCountryCode);
        } catch (error) {
          console.error(`Error getting calling code for ${selectedCountry}:`, error);
          formik.setFieldValue("country_code", "");
        }
      }
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (routeData?.name === "Edit" && employeeDetail) {
      formik.setValues({
        ...employeeDetail,
        role: employeeDetail?.role?._id,
      });
      setFile(employeeDetail?.imgUrl);

      // If editing, find the country that matches the country code
      if (employeeDetail?.country_code) {
        const countryCode = employeeDetail.country_code.replace('+', '');
        // Find country by calling code
        const matchingCountry = countries.find(country => {
          try {
            return getCountryCallingCode(country.isoCode) === countryCode;
          } catch (error) {
            return false;
          }
        });
        
        if (matchingCountry) {
          setSelectedCountry(matchingCountry.name);
        } else {
          // If no matching country is found, default to a placeholder
          formik.setFieldValue("country_code", `+${countryCode}`);
        }
      }
    }
  }, [employeeDetail, routeData]);

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    role: Yup.string().required("Role is required"),
    phoneNumber: Yup.string()
      .matches(/^\d*$/, "Phone number must contain only digits")
      .required("Phone number is required"),
    designation: Yup.string().required("Designation is required"),
    linkedinUrl: Yup.string().url("Enter a valid URL"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      role: null,
      designation: "",
      country_code: "",
      phoneNumber: "",
      linkedinUrl: "",
      imgUrl: "",
      status: true,
    },
    validationSchema, 
    onSubmit: (values) => {
      dispatch(setLoading(true));

      if (routeData?.name === "Add") {
        dispatch(
          createEmployee({
            ...values,
            imgUrl: file,
            phoneNumber: Number(values.phoneNumber),
          })
        )
          .then(() => {
            dispatch(setLoading(false));
            formik.resetForm();
            navigate(-1);
          })
          .catch(() => {
            dispatch(setLoading(false));
          });
      } else {
        const r_id = values._id;
        dispatch(updateEmployee({ r_id, formData: values }))
          .then(() => {
            dispatch(setLoading(false));
            formik.resetForm();
            navigate(-1);
          })
          .catch(() => {
            dispatch(setLoading(false));
          });
      }
    },
  });

  const handleReset = () => {
    formik.resetForm();
  };

  return (
    <Box  sx={{ height: "90vh", display: "flex", flexDirection: "column" }}>
      <Typography variant="h5" sx={{ p:2 }}>
        {routeData?.name} Employee
      </Typography>
      {/* <Divider/> */}
      
      <Grid container spacing={2} sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid
          container
          spacing={2}
          sx={{ p: 3,}}
        >
          <Grid item size={{md:6,xs:12}}>
            <Box>
              <Typography variant="h5" sx={{ mb: 2 }}>
                General Information
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="name">Full Name <span style={{ color: "red" }}>*</span></FormLabel>
                <TextField
                  id="name"
                  name="name"
                  variant="outlined"
                  placeholder="Ex: John Doe"
                  fullWidth
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel>Role <span style={{ color: "red" }}>*</span></FormLabel>
                <Autocomplete
                  disablePortal
                  options={roleOptions}
                  fullWidth
                  getOptionLabel={(option) => option.name}
                  value={
                    roleOptions.find(
                      (option) => option.id === formik.values.role
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue("role", newValue?.id || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Role"
                      onBlur={formik.handleBlur}
                      error={formik.touched.role && Boolean(formik.errors.role)}
                      helperText={formik.touched.role && formik.errors.role}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="phoneNumber">Phone Number <span style={{ color: "red" }}>*</span></FormLabel>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {/* Country Code Input */}
                  <TextField
                    id="country_code"
                    name="country_code"
                    variant="outlined"
                    placeholder="+1"
                    sx={{ width: "80px" }}
                    value={formik.values.country_code}
                    onChange={(e) => {
                      let inputCode = e.target.value.replace(/\D/g, "");
                      if (inputCode.length > 4) inputCode = inputCode.slice(0, 4);
              
                      const matchingCountry = countries.find((c) => {
                        try {
                          return getCountryCallingCode(c.isoCode) === inputCode;
                        } catch (error) {
                          return false;
                        }
                      });
              
                      formik.setFieldValue("country_code", inputCode ? `+${inputCode}` : "");
                      setSelectedCountry(matchingCountry ? matchingCountry.name : "");
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                    helperText={formik.touched.country_code && formik.errors.country_code}
                  />
              
                  {/* Country Selector */}
                  <Autocomplete
                    disablePortal
                    options={countries.map((country) => country.name)}
                    value={selectedCountry}
                    onChange={(event, newValue) => {
                      setSelectedCountry(newValue);
                      if (!newValue) {
                        // If country is cleared, reset country code as well
                        formik.setFieldValue("country_code", "");
                      } else {
                        const country = countries.find((c) => c.name === newValue);
                        if (country) {
                          try {
                            const newCountryCode = `+${getCountryCallingCode(country.isoCode)}`;
                            formik.setFieldValue("country_code", newCountryCode);
                          } catch (error) {
                            console.error(`Error getting calling code for ${newValue}:`, error);
                            formik.setFieldValue("country_code", "");
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Country"
                        size="small"
                        onBlur={formik.handleBlur}
                        error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                        helperText={formik.touched.country_code && formik.errors.country_code}
                      />
                    )}
                    sx={{ width: "300px" }}
                  />
              
                  {/* Phone Number Input */}
                  <TextField
                    type="number"
                    id="phoneNumber"
                    name="phoneNumber"
                    variant="outlined"
                    placeholder="Ex: 78676 67556"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                    helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                    onInput={(e) => {
                      e.target.value = e.target.value.slice(0, 10);
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    sx={{
                      flexGrow: 1,
                      '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                      '& input[type="number"]': {
                        MozAppearance: "textfield",
                      },
                    }}
                  />
                </Box>
              </FormControl>




              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="email">Email ID <span style={{ color: "red" }}>*</span></FormLabel>
                <TextField
                  id="email"
                  name="email"
                  variant="outlined"
                  placeholder="Ex: ex@gmail.com"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="designation">Designation <span style={{ color: "red" }}>*</span></FormLabel>
                <TextField
                  id="designation"
                  name="designation"
                  variant="outlined"
                  placeholder="Ex: Manager"
                  fullWidth
                  value={formik.values.designation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.designation &&
                    Boolean(formik.errors.designation)
                  }
                  helperText={
                    formik.touched.designation && formik.errors.designation
                  }
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel htmlFor="linkedinUrl">LinkedIn URL</FormLabel>
                <TextField
                  id="linkedinUrl"
                  name="linkedinUrl"
                  variant="outlined"
                  placeholder="Ex: https://"
                  fullWidth
                  value={formik.values.linkedinUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.linkedinUrl &&
                    Boolean(formik.errors.linkedinUrl)
                  }
                  helperText={
                    formik.touched.linkedinUrl && formik.errors.linkedinUrl
                  }
                />
              </FormControl>
            </Box>
          </Grid>
          <Grid
            item
            size={{md:6,xs:12}}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <CloudUpload
              placeholder="Upload Profile Image"
              file={file}
              setFile={setFile}
            />
          </Grid>
        </Grid>
        <Grid
         sx={{ justifyContent: "flex-end", display: "flex", gap: 3, mt: 4, p: 2 }}
         >
           <Button variant="outlined" onClick={handleReset}>
             Reset
           </Button>
           <Button type="submit" variant="contained">
             Submit
           </Button>
         </Grid>
       </form>
       </Grid>
     </Box>
   );
};

export default NewEmployee;
